// src/services/giftsApi.js

import { authorizedApi } from './index'; // Adjust the import path as necessary

export const giftsApi = authorizedApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Mutation to create a new gift.
     * Sends a POST request to /api/gifts/createGift with the gift details.
     */
    createGift: builder.mutation({
      query: (gift) => ({
        url: '/gifts/createGift',
        method: 'POST',
        body: gift,
        credentials: 'include',
      }),
      // Optionally, handle cache invalidation or updates here
      // For example, invalidating a "gifts" list query if you have one
      invalidatesTags: ['Gifts'],
    }),

    /**
     * Mutation to update an existing gift.
     * Sends a PUT request to /api/gifts/:giftId with the updated gift details.
     */
    updateGift: builder.mutation({
      query: ({ giftId, ...gift }) => ({
        url: `/gifts/${giftId}`,
        method: 'PUT',
        body: gift,
      }),
      transformResponse: (response) => {
        // Update the cache with the new gift data
        return response;
      },
      invalidatesTags: ['Gifts'],
      // Add optimistic updates
      onQueryStarted: async (
        { giftId, ...gift },
        { dispatch, queryFulfilled },
      ) => {
        // Optimistically update the cache
        const patchResult = dispatch(
          giftsApi.util.updateQueryData('getGift', giftId, (draft) => {
            Object.assign(draft.gift, gift);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    /**
     * Mutation to prepare a transaction for a gift.
     * Sends a POST request to /api/gifts/:giftId/prepareTransaction.
     */
    prepareTransaction: builder.mutation({
      query: (giftId) => ({
        url: `/gifts/${giftId}/prepareTransaction`,
        method: 'POST',
      }),
    }),

    /**
     * Mutation to prepare HTLC creation transaction.
     * Sends a POST request to /api/gifts/:giftId/prepareHTLCCreation.
     */
    prepareHTLCCreation: builder.mutation({
      query: ({ giftId, creatorWalletAddress }) => ({
        url: `/gifts/${giftId}/prepareHTLCCreation`,
        method: 'POST',
        body: { creatorWalletAddress },
      }),
    }),

    /**
     * Mutation to broadcast HTLC creation transaction.
     * Sends a POST request to /api/gifts/:giftId/broadcastHTLCCreation.
     */
    broadcastHTLCCreation: builder.mutation({
      query: ({ giftId, signedTransaction, creatorWalletAddress }) => ({
        url: `/gifts/${giftId}/broadcastHTLCCreation`,
        method: 'POST',
        body: { signedTransaction, creatorWalletAddress },
      }),
      invalidatesTags: ['Gifts'],
    }),

    /**
     * Mutation to prepare HTLC claim transaction.
     * Sends a POST request to /api/gifts/:giftId/prepareHTLCClaim.
     */
    prepareHTLCClaim: builder.mutation({
      query: ({ giftId, claimCode, sourceWallet }) => ({
        url: `/gifts/${giftId}/prepareHTLCClaim`,
        method: 'POST',
        body: { claimCode, sourceWallet },
      }),
    }),

    /**
     * Mutation to claim HTLC gift.
     * Sends a POST request to /api/gifts/:giftId/claimHTLC.
     */
    broadcastClaimHTLC: builder.mutation({
      query: ({ giftId, claimCode, signedTransaction, sourceWallet }) => ({
        url: `/gifts/${giftId}/claimHTLC`,
        method: 'POST',
        body: { claimCode, signedTransaction, sourceWallet },
      }),
      invalidatesTags: ['Gifts'],
    }),

    /**
     * Mutation to track the transfer status of a gift.
     * Sends a POST request to /api/gifts/trackTransfer with giftId and transactionHash.
     */
    trackTransfer: builder.mutation({
      query: (transfer) => ({
        url: '/gifts/trackTransfer',
        method: 'POST',
        body: transfer,
      }),
      // Optionally, handle cache invalidation or updates here
      invalidatesTags: ['Gifts'],
    }),

    /**
     * Mutation to broadcast and track a gift transfer transaction
     * Sends a POST request to /api/gifts/broadcastAndTrack
     */
    broadcastAndTrack: builder.mutation({
      query: ({ giftId, serializedTransaction }) => ({
        url: '/gifts/broadcastAndTrack',
        method: 'POST',
        body: { giftId, serializedTransaction },
      }),
      invalidatesTags: ['Gifts'],
    }),

    /**
     * Mutation to prepare swap transaction for HTLC gift creation.
     * Sends a POST request to /api/gifts/:giftId/swap
     */
    prepareGiftSwap: builder.mutation({
      query: ({ giftId, slippageBps = 'auto' }) => ({
        url: `/gifts/${giftId}/swap`,
        method: 'POST',
        body: { slippageBps },
      }),
    }),

    /**
     * Mutation to broadcast swap transaction for HTLC gift.
     * Sends a POST request to /api/gifts/:giftId/broadcastSwap
     */
    broadcastGiftSwap: builder.mutation({
      query: ({ giftId, serializedTransaction }) => ({
        url: `/gifts/${giftId}/broadcastSwap`,
        method: 'POST',
        body: { serializedTransaction },
      }),
      invalidatesTags: ['Gifts'],
    }),

    getGift: builder.query({
      query: (giftId) => `/gifts/${giftId}`,
      providesTags: (result, error, giftId) => [{ type: 'Gifts', id: giftId }],
    }),
    getGifts: builder.query({
      query: () => '/gifts', // calls GET /api/gifts
      providesTags: ['Gifts'],
    }),
  }),
  overrideExisting: true,
});

// Export hooks for usage in functional components
export const {
  useCreateGiftMutation,
  useUpdateGiftMutation,
  usePrepareTransactionMutation,
  usePrepareHTLCCreationMutation,
  useBroadcastHTLCCreationMutation,
  usePrepareHTLCClaimMutation,
  useBroadcastClaimHTLCMutation,
  useTrackTransferMutation,
  useGetGiftQuery,
  useLazyGetGiftQuery,
  useGetGiftsQuery,
  useBroadcastAndTrackMutation,
  usePrepareGiftSwapMutation,
  useBroadcastGiftSwapMutation,
} = giftsApi;
